import debug from 'debug';
import EventEmitter from 'eventemitter3';

type PromiseQueueOptions = {
  concurrentPromises: number;
};

type TaskDefinition = {
  id: string | number;
  run: () => Promise<unknown>;
};

type CompletedTask = {
  id: TaskDefinition['id'];
  result: Awaited<ReturnType<TaskDefinition['run']>>;
};

type Task = TaskDefinition & {
  promise: Promise<CompletedTask>;
  resolve: CallableFunction;
  reject: CallableFunction;
};

const fileDebug = debug('durable:utils:promiseQueue');

const PromiseQueueEvents = {
  FINISHED_TASK: 'FINISHED_TASK',
  QUEUE_COMPLETE: 'QUEUE_COMPLETE',
} as const;

/**
 * Put your promises in a queue
 */
export class PromiseQueue extends EventEmitter {
  taskIds = new Set();

  queue: Task[] = [];

  runningQueue: Task[] = [];

  concurrentPromises = 2;

  protected queueCompletePromise: Promise<boolean> | null = null;

  protected queueCompleted = false;

  constructor(options: Partial<PromiseQueueOptions> = {}) {
    super();
    if (options.concurrentPromises) {
      this.concurrentPromises = options.concurrentPromises;
    }
    fileDebug(`concurrency: ${this.concurrentPromises}`);
  }

  get size() {
    return this.queue.length + this.runningQueue.length;
  }

  /**
   * Determine if we can run anymore tasks right now
   */
  checkIfWeCanRunTasks() {
    const methodDebug = fileDebug.extend('checkIfWeCanRunTasks');
    methodDebug('queue size', this.size);
    if (this.queue.length > 0 && this.runningQueue.length < this.concurrentPromises) {
      // move the first task to running...
      const task = this.queue.shift();
      if (task) {
        this.runningQueue.push(task);
        methodDebug(`Running task: ${task.id}`, {
          running: this.runningQueue.length,
          queued: this.queue.length,
        });
        task
          .run()
          .then(this.handleSuccessfulTask.bind(this, task))
          .catch(this.handleFailedTask.bind(this, task));
      }
    } else if (this.queue.length === 0 && this.runningQueue.length === 0) {
      methodDebug('queue completed');
      this.emit(PromiseQueueEvents.QUEUE_COMPLETE);
    }
  }

  /**
   * Add a new task to the queue.
   *
   * The taskCompletionPromise in the object returned by this method will
   * resolve/reject when the task's `run` method resolves.
   */
  enqueue(task: TaskDefinition): { taskCompletionPromise: Promise<CompletedTask> } {
    const methodDebug = fileDebug.extend('enqueue');
    if (this.taskIds.has(task.id)) {
      throw new Error(`Duplicate task ID: ${task.id}`);
    }

    let resolver = null;
    let rejecter = null;
    const taskCompletionPromise = new Promise<CompletedTask>((resolve, reject) => {
      resolver = resolve;
      rejecter = reject;
    });

    // make types happy...
    if (!resolver || !rejecter) {
      throw new Error('Could not get resolver/rejecter');
    }

    this.taskIds.add(task.id);
    this.queue.push({
      ...task,
      resolve: resolver,
      reject: rejecter,
      promise: taskCompletionPromise,
    });
    methodDebug(`Added task ${task.id}`, {
      running: this.runningQueue.length,
      queued: this.queue.length,
    });

    // create (or recreate) the queue complete promise now that something is enqueued
    if (!this.queueCompletePromise || (this.queueCompletePromise && this.queueCompleted)) {
      this.queueCompletePromise = new Promise((resolve) => {
        this.removeAllListeners(PromiseQueueEvents.QUEUE_COMPLETE);
        this.once(PromiseQueueEvents.QUEUE_COMPLETE, () => {
          resolve(true);
          this.queueCompleted = true;
        });
      });
    }
    this.queueCompleted = false;
    this.checkIfWeCanRunTasks();
    return { taskCompletionPromise };
  }

  handleFailedTask(task: Task, error: unknown) {
    fileDebug.extend('handleFailedTask')(`Task ${task.id} failed`);
    this.runningQueue = this.runningQueue.filter((queuedTask) => queuedTask.id !== task.id);
    this.taskIds.delete(task.id);
    this.emit(PromiseQueueEvents.FINISHED_TASK, task);
    task.reject({ id: task.id, error });
    this.checkIfWeCanRunTasks();
  }

  handleSuccessfulTask(task: Task, result: unknown) {
    fileDebug.extend('handleSuccessfulTask')(`Task ${task.id} finished successfully`);
    // update running queue by removing the current task
    this.runningQueue = this.runningQueue.filter((queuedTask) => queuedTask.id !== task.id);
    this.taskIds.delete(task.id);
    this.emit(PromiseQueueEvents.FINISHED_TASK, task);
    task.resolve({
      id: task.id,
      result,
    });
    this.checkIfWeCanRunTasks();
  }

  /**
   * Await for the queue to complete all queued tasks.
   */
  async queueComplete() {
    if (!this.queueCompletePromise) {
      return Promise.resolve(true);
    }

    return this.queueCompletePromise;
  }
}
